import React, { useState } from 'react';
import { 
  EyeSlashIcon, 
  EyeIcon, 
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { moderateQuestion, moderateReply, deleteQuestion } from '../../../services/firebase/forum';
import { useAuthContext } from '../../../hooks/useAuthContext';

/**
 * ModerationControls component for admin moderation actions
 * Shows hide/unhide, delete controls for questions and replies
 */
const ModerationControls = ({ 
  item, 
  type, // 'question' or 'reply'
  onModerationAction,
  className = '' 
}) => {
  const { user } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [reason, setReason] = useState('');

  // Check if user is admin (this would be replaced with actual admin check)
  const isAdmin = user?.role === 'admin' || user?.isAdmin;

  if (!isAdmin) {
    return null;
  }

  const handleModerationAction = async (action) => {
    if (action === 'delete' || (action === 'hide' && item.status !== 'hidden')) {
      setActionType(action);
      setShowReasonModal(true);
      return;
    }

    await executeModerationAction(action, '');
  };

  const executeModerationAction = async (action, reasonText) => {
    setIsLoading(true);
    
    try {
      let result;
      
      switch (action) {
        case 'hide':
          if (type === 'question') {
            result = await moderateQuestion(item.id, user.uid, true, reasonText);
          } else {
            result = await moderateReply(item.id, user.uid, true, reasonText);
          }
          break;
          
        case 'unhide':
          if (type === 'question') {
            result = await moderateQuestion(item.id, user.uid, false, reasonText);
          } else {
            result = await moderateReply(item.id, user.uid, false, reasonText);
          }
          break;
          
        case 'delete':
          if (type === 'question') {
            result = await deleteQuestion(item.id, user.uid, reasonText);
          } else {
            // For replies, we use the moderate function with deleted status
            result = await moderateReply(item.id, user.uid, true, reasonText);
          }
          break;
          
        default:
          throw new Error('Invalid action');
      }

      if (result.success) {
        if (onModerationAction) {
          onModerationAction(item.id, action, result.data);
        }
      } else {
        console.error('Moderation action failed:', result.error);
        // You could show a toast notification here
      }
    } catch (error) {
      console.error('Error executing moderation action:', error);
    } finally {
      setIsLoading(false);
      setShowReasonModal(false);
      setReason('');
      setActionType(null);
    }
  };

  const handleReasonSubmit = () => {
    if (actionType) {
      executeModerationAction(actionType, reason);
    }
  };

  const isHidden = item.status === 'hidden';
  const isDeleted = item.status === 'deleted';

  if (isDeleted) {
    return (
      <div className={`flex items-center space-x-2 text-xs text-red-600 dark:text-red-400 ${className}`}>
        <TrashIcon className="w-4 h-4" />
        <span>Deleted by admin</span>
      </div>
    );
  }

  return (
    <>
      <div className={`flex items-center space-x-2 ${className}`}>
        {/* Hide/Unhide Button */}
        <button
          onClick={() => handleModerationAction(isHidden ? 'unhide' : 'hide')}
          disabled={isLoading}
          className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
            isHidden
              ? 'text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100 dark:text-green-400 dark:bg-green-900/20 dark:hover:bg-green-900/30'
              : 'text-yellow-600 hover:text-yellow-700 bg-yellow-50 hover:bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={isHidden ? 'Unhide content' : 'Hide content'}
        >
          {isHidden ? (
            <>
              <EyeIcon className="w-3 h-3" />
              <span>Unhide</span>
            </>
          ) : (
            <>
              <EyeSlashIcon className="w-3 h-3" />
              <span>Hide</span>
            </>
          )}
        </button>

        {/* Delete Button */}
        <button
          onClick={() => handleModerationAction('delete')}
          disabled={isLoading}
          className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors
                     text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 
                     dark:text-red-400 dark:bg-red-900/20 dark:hover:bg-red-900/30
                     ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Delete content"
        >
          <TrashIcon className="w-3 h-3" />
          <span>Delete</span>
        </button>

        {isHidden && (
          <div className="flex items-center space-x-1 text-xs text-yellow-600 dark:text-yellow-400">
            <EyeSlashIcon className="w-3 h-3" />
            <span>Hidden</span>
          </div>
        )}
      </div>

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {actionType === 'delete' ? 'Delete Content' : 'Hide Content'}
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Please provide a reason for this moderation action:
            </p>
            
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for moderation..."
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg 
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-purple-500 focus:border-transparent
                         resize-none"
              rows={3}
            />
            
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => {
                  setShowReasonModal(false);
                  setReason('');
                  setActionType(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                           bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 
                           rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReasonSubmit}
                disabled={!reason.trim() || isLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors
                           ${actionType === 'delete' 
                             ? 'bg-red-600 hover:bg-red-700' 
                             : 'bg-yellow-600 hover:bg-yellow-700'
                           }
                           ${(!reason.trim() || isLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoading ? 'Processing...' : (actionType === 'delete' ? 'Delete' : 'Hide')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModerationControls;